import React, { useEffect } from "react";
import { ArrowUpRight, Megaphone } from "lucide-react";
import { useToast } from "../hooks/use-toast.ts";
import EmptyState from "./EmptyState.tsx";
import OfferPageTable from "./offers/OfferPageTable.tsx";
import { useStripeConnection } from "../hooks/useStripeConnection.ts";
import { useServerPaginatedOfferPages } from "../hooks/useServerPaginatedOfferPages.ts";
import { updateOfferPageStatus } from "../services/paginatedOfferPageService.ts";
import TablePagination from "./ui/tablePagination.tsx";
import { useOfferCreatedListener } from "../hooks/useGlobalEvents.ts";

interface OfferPageListServerPaginatedProps {
  filterStatus?: string;
  searchQuery?: string;
  onCopyLink: (id: string) => void;
  onArchive: (id: string) => void;
  onEdit?: (id: string) => void;
}

const OfferPageListServerPaginated: React.FC<
  OfferPageListServerPaginatedProps
> = ({
  filterStatus = "all",
  searchQuery = "",
  onCopyLink: _onCopyLink,
  onArchive: _onArchive,
  onEdit,
}) => {
  const { toast } = useToast();
  const { isConnected: stripeConnected, isLoading } = useStripeConnection();

  useOfferCreatedListener(() => {
    refresh();
  });

  const {
    offerPages,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    hasNextPage,
    hasPreviousPage,
    startItem,
    endItem,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    setItemsPerPage,
    setFilters,
    refresh,
  } = useServerPaginatedOfferPages(10, {
    status: filterStatus,
    searchQuery,
  });

  useEffect(() => {
    const newFilters: {
      status?: string;
      searchQuery?: string;
    } = {};

    if (filterStatus && filterStatus !== "all") {
      newFilters.status = filterStatus;
    }
    if (searchQuery && searchQuery.trim() !== "") {
      newFilters.searchQuery = searchQuery;
    }

    setFilters(newFilters);
  }, [filterStatus, searchQuery, setFilters]);

    const handleCopyId = (id: string) => {
        _onCopyLink(id);
    };

  const handleArchiveToggle = async (id: string) => {
    try {
      const offerPage = offerPages.find((page) => page.id === id);
      if (!offerPage) {
        toast({
          title: "Bundle Page not found",
          description: `No bundle page found with ID ${id}.`,
          variant: "destructive",
        });
        return;
      }

      const newStatus = offerPage.status === "active" ? "archived" : "active";
      const success = await updateOfferPageStatus(id, newStatus);

      if (success) {
        toast({
          title: `Bundle Page ${
            newStatus === "active" ? "activated" : "archived"
          }`,
          description: `Bundle Page ID ${id} has been ${newStatus}.`,
        });
        refresh();
      } else {
        toast({
          title: "Operation failed",
          description: `Failed to update bundle page status.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error toggling bundle page status:", error);
      toast({
        title: "Error",
        description: "Failed to toggle bundle page status.",
        variant: "destructive",
      });
    }
  };

  if (loading || isLoading) {
    return (
      <div className="text-center py-12 bg-card">
        <p className="text-muted-foreground">Loading bundle pages...</p>
      </div>
    );
  }

  if (!stripeConnected) {
    return (
      <EmptyState
        title="Connect your Stripe account"
        description="To start creating bundle pages, connect your Stripe account first."
        actionLabel="Connect Stripe Account"
        identifier
        icon={<ArrowUpRight className="h-12 w-12" />}
      />
    );
  }

  if (error) {
    return (
      <EmptyState
        title="Error loading bundle pages"
        description={error}
        actionLabel="Try Again"
        icon={<Megaphone className="h-12 w-12" />}
      />
    );
  }

  if (totalItems === 0) {
    return (
      <EmptyState
        title="No Bundles Found"
        description="There are no bundles available. Create your first bundle to get started selling multiple products together."
        actionLabel="Create Bundle"
        icon={<Megaphone className="h-12 w-12" />}
      />
    );
  }
  return (
    <>
      <OfferPageTable
        offerPages={offerPages}
        onCopyLink={handleCopyId}
        onArchive={handleArchiveToggle}
        onCopyId={handleCopyId}
        onEdit={onEdit}
      />
      <TablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        startItem={startItem}
        endItem={endItem}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={goToPage}
        onItemsPerPageChange={setItemsPerPage}
        onFirstPage={goToFirstPage}
        onLastPage={goToLastPage}
        onNextPage={goToNextPage}
        onPreviousPage={goToPreviousPage}
      />
    </>
  );
};

export default OfferPageListServerPaginated;
